var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import 'react-quill/dist/quill.snow.css';
import './TextEditor.css';
import { Typography } from '@mui/material';
import { ShowContent } from '../ShowContent';
import Quill from 'quill';
export function stripHtml(html) {
    var div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
}
export var TextEditor = forwardRef(function (_a, ref) {
    var _b = _a.placeholder, placeholder = _b === void 0 ? 'Informe o texto' : _b, _c = _a.formats, formats = _c === void 0 ? [
        'header',
        'font',
        'size',
        'bold',
        'italic',
        'underline',
        'strike',
        'blockquote',
        'list',
        'bullet',
        'indent',
        'link',
        'image',
        'clean',
    ] : _c, _d = _a.showHeaderOptions, showHeaderOptions = _d === void 0 ? true : _d, _e = _a.showImageButton, showImageButton = _e === void 0 ? true : _e, _f = _a.showFontOptions, showFontOptions = _f === void 0 ? true : _f, _g = _a.showListOptions, showListOptions = _g === void 0 ? true : _g, _h = _a.showCleanButton, showCleanButton = _h === void 0 ? true : _h, _j = _a.showLinkButton, showLinkButton = _j === void 0 ? true : _j, _k = _a.displayCount, displayCount = _k === void 0 ? false : _k, _l = _a.disabled, disabled = _l === void 0 ? false : _l, _m = _a.helperText, helperText = _m === void 0 ? '' : _m, _o = _a.error, error = _o === void 0 ? false : _o, _p = _a.label, label = _p === void 0 ? '' : _p, onChange = _a.onChange, value = _a.value, maxChar = _a.maxChar;
    useImperativeHandle(ref, function () { return ({
        focus: function () { var _a; return (_a = quillRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
        getEditor: function () { var _a; return (_a = quillRef.current) === null || _a === void 0 ? void 0 : _a.getEditor(); },
        getHtml: function () { var _a; return (_a = quillRef.current) === null || _a === void 0 ? void 0 : _a.getEditor().root.innerHTML; },
        clear: function () { var _a; return (_a = quillRef.current) === null || _a === void 0 ? void 0 : _a.getEditor().setText(''); }
    }); });
    var LimitModule = function (quill, options) {
        var getTextLength = function () {
            var text = quill.getText() || '';
            return text.trim().length;
        };
        quill.clipboard.addMatcher(Node.ELEMENT_NODE, function (node, delta) {
            var currentLength = getTextLength();
            var insertLength = delta.ops.reduce(function (len, op) {
                return len + (typeof op.insert === 'string' ? op.insert.length : 0);
            }, 0);
            if (currentLength + insertLength > options.maxChar) {
                var allowed = options.maxChar - currentLength;
                var used = 0;
                var newOps = [];
                for (var _i = 0, _a = delta.ops; _i < _a.length; _i++) {
                    var op = _a[_i];
                    if (typeof op.insert !== 'string')
                        continue;
                    var remaining = allowed - used;
                    if (remaining <= 0)
                        break;
                    var slicedInsert = op.insert.slice(0, remaining);
                    newOps.push(__assign(__assign({}, op), { insert: slicedInsert }));
                    used += slicedInsert.length;
                }
                delta.ops = newOps;
            }
            return delta;
        });
        quill.on('text-change', function (delta, oldDelta, source) {
            if (source !== 'user')
                return;
            var currentLength = getTextLength();
            if (currentLength > options.maxChar) {
                quill.history.undo();
            }
        });
    };
    var quillRef = useRef(null);
    Quill.register('modules/limit', LimitModule);
    var modules = useCallback(function () {
        var toolbar = [];
        if (showHeaderOptions)
            toolbar.push([{ header: '1' }, { header: '2' }, showFontOptions ? { font: [] } : null].filter(Boolean));
        toolbar.push([{ size: [] }]);
        toolbar.push(['bold', 'italic', 'underline', 'strike', 'blockquote']);
        if (showListOptions) {
            toolbar.push([
                { list: 'ordered' },
                { list: 'bullet' },
                { indent: '-1' },
                { indent: '+1' },
            ]);
        }
        var linkImageRow = [
            showLinkButton ? 'link' : null,
            showImageButton ? 'image' : null,
        ].filter(Boolean);
        if (linkImageRow.length)
            toolbar.push(linkImageRow);
        if (showCleanButton)
            toolbar.push(['clean']);
        return __assign({ toolbar: toolbar, clipboard: { matchVisual: false } }, (maxChar && { limit: { maxChar: maxChar } }));
    }, [showImageButton, showLinkButton, showFontOptions, showHeaderOptions, showListOptions, showCleanButton, maxChar]);
    var defaultTheme = createTheme();
    var handleChange = function (content) {
        onChange === null || onChange === void 0 ? void 0 : onChange(content);
    };
    var color = useMemo(function () {
        return (error ? 'error' : 'textPrimary');
    }, [error]);
    return (_jsxs(_Fragment, { children: [_jsxs(Typography, __assign({ variant: 'subtitle2', align: 'left', color: color }, { children: ["\u00A0\u00A0\u00A0", label] })), _jsx(ThemeProvider, __assign({ theme: defaultTheme }, { children: _jsxs("div", __assign({ className: "text-editor-container ".concat(disabled ? 'disabled' : '', " ").concat(error ? 'has-error' : '') }, { children: [_jsx(ReactQuill, { style: {
                                width: '100%',
                                maxWidth: '100%',
                                overflowWrap: 'break-word',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                            }, placeholder: placeholder, onChange: handleChange, value: value !== null && value !== void 0 ? value : '', readOnly: disabled, modules: modules(), formats: formats, ref: quillRef, theme: "snow" }), _jsx(ShowContent, __assign({ show: !!helperText }, { children: _jsx("p", __assign({ className: "editor-text ".concat(error ? 'error' : ''), style: {
                                    overflowWrap: 'break-word',
                                    wordBreak: 'break-word',
                                    maxWidth: '100%',
                                } }, { children: helperText })) })), _jsx(ShowContent, __assign({ show: !!maxChar && displayCount }, { children: _jsxs("p", __assign({ className: "editor-char-count" }, { children: [stripHtml(value !== null && value !== void 0 ? value : '').length, "/", maxChar] })) }))] })) }))] }));
});
